import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  getUnreadCounts,
  getAnnouncements,
  markAllAnnouncementsRead,
  type UnreadCountsResponse,
  type AnnouncementModel,
} from 'src/service/messaging'

export const useClientMessagingStore = defineStore('client.messaging', () => {
  const isInitialzed = ref(false)
  const totalUnreadCount = ref(0)
  const isMessengerOpen = ref(false)
  const pendingUnreadByConversation = ref<Record<string, number>>({})

  // Active tab in messenger
  type TabKey = 'room' | 'admin' | 'announcements'
  const activeTab = ref<TabKey>('room')

  // Client announcements state
  const announcements = ref<AnnouncementModel[]>([])
  const isLoadingAnnouncements = ref(false)
  const isLoadingMoreAnnouncements = ref(false)
  const announcementsContinuationToken = ref<string | null>(null)
  const hasMoreAnnouncements = computed(
    () => !!announcementsContinuationToken.value,
  )

  // Detailed unread counts for messenger tab badges
  const unreadCountBreakdown = ref<UnreadCountsResponse>({
    total: 0,
    roomThreads: 0,
    adminThreads: 0,
  })

  const unreadMessagesCount = computed<number>(() => {
    return totalUnreadCount.value
  })

  const clearUnreadMessages = () => {
    totalUnreadCount.value = 0
    unreadCountBreakdown.value = { total: 0, roomThreads: 0, adminThreads: 0 }
    announcements.value = []
    announcementsContinuationToken.value = null
    activeTab.value = 'room'
  }

  const setIsInitialized = (initialized: boolean) => {
    isInitialzed.value = initialized
  }

  const setUnreadCountBreakdown = (counts: UnreadCountsResponse) => {
    unreadCountBreakdown.value = counts
    totalUnreadCount.value = counts.total
  }

  const clearTabUnread = (
    tab: 'roomThreads' | 'adminThreads' | 'announcements',
  ) => {
    const current = unreadCountBreakdown.value
    const delta = current[tab] ?? 0
    if (delta <= 0) return
    unreadCountBreakdown.value = {
      ...current,
      [tab]: 0,
      total: Math.max(0, current.total - delta),
    }
    totalUnreadCount.value = unreadCountBreakdown.value.total
  }

  const incrementTabUnread = (
    tab: 'roomThreads' | 'adminThreads' | 'announcements',
  ) => {
    const current = unreadCountBreakdown.value
    unreadCountBreakdown.value = {
      ...current,
      [tab]: (current[tab] ?? 0) + 1,
      total: current.total + 1,
    }
    totalUnreadCount.value = unreadCountBreakdown.value.total
  }

  const refreshUnreadCounts = async () => {
    try {
      const response = await getUnreadCounts()
      setUnreadCountBreakdown(response)
    } catch {
      // Silently fail — badge will just show 0
    }
  }

  const fetchAnnouncements = async () => {
    isLoadingAnnouncements.value = true
    try {
      const response = await getAnnouncements(20)
      announcements.value = [
        ...(response.pinnedItems ?? []),
        ...(response.items || []),
      ]
      announcementsContinuationToken.value = response.continuationToken ?? null
    } catch {
      announcements.value = []
      announcementsContinuationToken.value = null
    } finally {
      isLoadingAnnouncements.value = false
    }
  }

  const fetchMoreAnnouncements = async () => {
    if (
      !announcementsContinuationToken.value ||
      isLoadingMoreAnnouncements.value
    )
      return
    isLoadingMoreAnnouncements.value = true
    try {
      const response = await getAnnouncements(
        20,
        announcementsContinuationToken.value,
      )
      announcements.value = [...announcements.value, ...(response.items || [])]
      announcementsContinuationToken.value = response.continuationToken ?? null
    } catch {
      // keep existing announcements on error
    } finally {
      isLoadingMoreAnnouncements.value = false
    }
  }

  const markAnnouncementsRead = async () => {
    clearTabUnread('announcements')
    void markAllAnnouncementsRead().catch(() => {})
  }

  const handleNewAnnouncement = async () => {
    if (isMessengerOpen.value && activeTab.value === 'announcements') {
      await fetchAnnouncements()
      void markAllAnnouncementsRead().catch(() => {})
    } else {
      incrementTabUnread('announcements')
    }
  }

  const initialize = async () => {
    if (isInitialzed.value) return

    try {
      const response = await getUnreadCounts()
      setUnreadCountBreakdown(response)
      isInitialzed.value = true
    } catch {
      // Don't set initialized to true on error, allow retry
    }
  }

  return {
    isInitialzed,
    unreadMessagesCount,
    totalUnreadCount,
    isMessengerOpen,
    unreadCountBreakdown,
    clearUnreadMessages,
    setIsInitialized,
    setUnreadCountBreakdown,
    clearTabUnread,
    incrementTabUnread,
    initialize,
    refreshUnreadCounts,
    pendingUnreadByConversation,
    activeTab,
    announcements,
    isLoadingAnnouncements,
    isLoadingMoreAnnouncements,
    hasMoreAnnouncements,
    fetchAnnouncements,
    fetchMoreAnnouncements,
    markAnnouncementsRead,
    handleNewAnnouncement,
  }
})
